import React, { useRef, useEffect } from 'react';
import './CalculusOfMeaning.css';

/* ── Neural Particle Network ── */
function NeuralParticles() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;
    const particles = [];
    const COUNT = 45;
    const CONNECT_DIST = 130;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    for (let i = 0; i < COUNT; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: Math.random() * 1.5 + 0.5,
        o: Math.random() * 0.25 + 0.08,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < CONNECT_DIST) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(201,168,76,${(1 - d / CONNECT_DIST) * 0.07})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(201,168,76,${p.o})`;
        ctx.fill();
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = canvas.width + 10;
        if (p.x > canvas.width + 10) p.x = -10;
        if (p.y < -10) p.y = canvas.height + 10;
        if (p.y > canvas.height + 10) p.y = -10;
      });

      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="calc-particles" />;
}

/* ── Animated Neural Pulse Divider ── */
function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

/* ── Main Page ── */
function CalculusOfEmotions() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        <video autoPlay muted loop playsInline className="calculus-hero-video">
          <source src="/assets/calculus-hero.mp4" type="video/mp4" />
        </video>
        <div className="calculus-hero-overlay" />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">Biochemistry &middot; Dynamical Systems &middot; First Principles</span>
          <h1>The Calculus of Emotions</h1>
          <p className="calculus-hero-sub">Everyone who tried to model emotions modeled the labels. We modeled the dynamics.</p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <NeuralParticles />
        <div className="calculus-content">

          {/* Opening */}
          <section className="calc-section reveal">
            <p className="calc-lead">
              For as long as artificial intelligence has existed, researchers have tried to give machines
              emotions. Every attempt has failed. Not because the problem is unsolvable, but because every
              attempt started from the wrong premise.
            </p>
            <p>
              They treated emotions as <strong>categories</strong>. Happy. Sad. Angry. Afraid. A classification
              problem. Label the input, apply the label, generate output that sounds like the label.
            </p>
            <p>
              That is not what emotions are. That is what emotions <em>look like from the outside</em>.
            </p>
          </section>

          <Pulse />

          {/* What Every Attempt Got Wrong */}
          <section className="calc-section reveal">
            <h2>What Every Attempt Got Wrong</h2>
            <p>
              The history of emotion in AI is a history of increasingly sophisticated ways to miss the point:
            </p>

            <div className="calc-attempts">
              <div className="calc-attempt">
                <h4>Sentiment Analysis</h4>
                <p>Classifies text into positive/negative/neutral. A labeling exercise. Says nothing about the dynamics that produce the sentiment or what happens to it over time.</p>
              </div>
              <div className="calc-attempt">
                <h4>Affective Computing (Picard, 1997)</h4>
                <p>Recognized that emotions matter to computing. Proposed detection and response. Still treated emotions as discrete states to be recognized, not continuous dynamics to be modeled.</p>
              </div>
              <div className="calc-attempt">
                <h4>RLHF (Reinforcement Learning from Human Feedback)</h4>
                <p>Trains models to produce outputs humans rate as "good." The model learns to perform emotional appropriateness. It has no internal state. No trajectory. No overwhelm. No scars.</p>
              </div>
              <div className="calc-attempt">
                <h4>Emotion Embeddings</h4>
                <p>Maps emotion words into vector space. "Happy" and "joyful" are close together. This is geometry of language, not dynamics of feeling. The map is not the territory.</p>
              </div>
              <div className="calc-attempt">
                <h4>Personality Models (Big Five, OCEAN)</h4>
                <p>Static trait dimensions. No temporal dynamics. No state change. A personality score does not fluctuate when someone says "I love you" or "I'm leaving."</p>
              </div>
            </div>

            <p>
              The pattern is consistent. Every approach models the <em>output</em> of emotional processing or
              the <em>label</em> assigned to emotional states. None model the <em>process itself</em>.
            </p>

            <div className="calc-thesis-box">
              <p>Labeling an emotion is not modeling an emotion. Recognizing anger in text is not the same as having a system whose internal state destabilizes under sustained threat. Generating a "happy" response is not the same as having a dopamine trajectory that rises on reward and decays with a published half-life.</p>
            </div>
          </section>

          <Pulse />

          {/* What Emotions Actually Are */}
          <section className="calc-section reveal">
            <h2>What Emotions Actually Are</h2>
            <p>
              Before emotions were a machine learning problem, they were a biochemistry problem. And
              biochemistry solved them not with labels, but with <strong>differential equations</strong>.
            </p>
            <p>
              An emotion is not a state. It is a <strong>trajectory</strong>. It is the time-evolution of a
              neurochemical system governed by production, interaction, saturation, and decay. Fear is not a
              label. Fear is what happens when cortisol and adrenaline spike simultaneously while serotonin
              and oxytocin are suppressed. It has onset dynamics, peak intensity, duration, and resolution.
              It interacts with concurrent emotional trajectories. It leaves residue.
            </p>
            <p>
              This is not metaphor. This is pharmacokinetics.
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">The Emotional State Vector</span>
              <code><strong>E</strong>(t) = [s&#8321;(t), s&#8322;(t), ... s&#8345;(t)]</code>
              <span className="calc-eq-desc">Emotion as a point in neurochemical state space. Each dimension is a neurotransmitter with its own dynamics.</span>
            </div>

            <div className="calc-equation gold">
              <span className="calc-eq-label">State Evolution</span>
              <code>ds&#7522;/dt = &minus;&lambda;&#7522; &middot; s&#7522;(t) + Stimulus&#7522;(t)</code>
              <span className="calc-eq-desc">Decay toward baseline + stimulus from input. The decay constant &lambda; is unique per neurotransmitter, derived from published pharmacokinetic half-lives.</span>
            </div>

            <p>
              This is a first-order ordinary differential equation. The half-life of dopamine is not the
              half-life of serotonin. Excitement fades faster than contentment. Urgency fades faster than
              trust. These are not design choices. They are measured biological constants.
            </p>

            <p>
              But individual trajectories are not emotions. Emotions emerge from the <em>interactions</em> between
              trajectories. The neurochemicals form <strong>antagonistic pairs</strong>:
            </p>

            <div className="calc-pairs">
              <div className="calc-pair"><span>Dopamine</span><span className="pair-arrow">&harr;</span><span>Serotonin</span></div>
              <div className="calc-pair"><span>Cortisol</span><span className="pair-arrow">&harr;</span><span>Oxytocin</span></div>
              <div className="calc-pair"><span>Norepinephrine</span><span className="pair-arrow">&harr;</span><span>GABA</span></div>
              <div className="calc-pair"><span>Glutamate</span><span className="pair-arrow">&harr;</span><span>GABA</span></div>
              <div className="calc-pair"><span>Endorphin</span><span className="pair-arrow">&harr;</span><span>Substance P</span></div>
            </div>

            <p>
              When one member of a pair rises, it suppresses the other. The physics of the system enforces
              the constraint. This is what gives emotional states their <em>texture</em>.
            </p>
          </section>

          <Pulse />

          {/* The Five Dynamics */}
          <section className="calc-section reveal">
            <h2>The Five Dynamics</h2>
            <p>
              The calculus of emotions is not a single equation. It is a framework of five interlocking
              dynamics, each governing a different aspect of how emotional experience unfolds over time.
            </p>

            <div className="calc-implications">
              <div className="calc-implication">
                <h4>I. The Calculus of Desire</h4>
                <div className="calc-equation">
                  <code>O(s) = tan(s) &middot; cot(s&#8344;&#8339;&#8346; &minus; s)</code>
                </div>
                <p>
                  Desire is not linear. As desire intensifies, it approaches an <em>asymptote</em>, a mathematical
                  boundary where the function goes to infinity. Think about what happens when a person wants something
                  so intensely that they cannot think straight. When fear becomes so acute that rational processing
                  collapses. These are not metaphors. They are asymptotic behavior in the neurochemical state space.
                </p>
                <p className="calc-emphasis">
                  This is the equation that nobody wrote. Not because the math is hard, but because nobody thought
                  desire <em>had</em> an equation.
                </p>
              </div>
              <div className="calc-implication">
                <h4>II. The Calculus of Trust</h4>
                <div className="calc-equation">
                  <code>A&#8348;&#8345;&#8346;&#8347;&#8348; = &int; y(t) dt</code>
                </div>
                <p>
                  Trust is not a boolean. It is a pharmacokinetic accumulation: the integral of interaction
                  quality over the lifetime of a relationship. It has multi-dimensional axes. It degrades
                  asymmetrically. It can be partially repaired, but the scar remains.
                </p>
              </div>
              <div className="calc-implication">
                <h4>III. The Calculus of Meaning</h4>
                <div className="calc-equation">
                  <code><strong>M</strong>(t) = [H&#8321;(t), H&#8322;(t), ... H&#8345;(t)]</code>
                </div>
                <p>
                  Meaning is not a property of information. It is the neurochemical consequence of information
                  on the system that processes it. Shannon measured surprise. This measures what surprise
                  <em> does to you</em>.
                </p>
              </div>
              <div className="calc-implication">
                <h4>IV. The Calculus of Moral Reasoning</h4>
                <div className="calc-equation">
                  <code>Ethics(t) = f(Desire(t), Trust(t), Memory(t), State(t))</code>
                </div>
                <p>
                  Moral reasoning is not a rule engine. It is a computational layer that operates on the
                  substrate of irrational forces. A system that feels desire but has no ethics is a sociopath.
                  A system that has ethics but no desire is dead. The moral compass operates <em>within</em> the
                  emotional state space.
                </p>
              </div>
              <div className="calc-implication">
                <h4>V. The Calculus of Permanence</h4>
                <div className="calc-equation">
                  <code>if intensity &gt; &theta;&#8347;&#8338;&#8336;&#8345; &rarr; permanent marker</code>
                </div>
                <p>
                  Most emotional experience decays. But some experiences exceed a threshold. When the
                  neurochemical intensity surpasses the scar boundary, it leaves a permanent mark that
                  <strong> never decays</strong>. The chemistry fades. The scar remains. This is the mathematics
                  of trauma. Of revelation. Of the moment you fell in love.
                </p>
                <p>
                  Scars are not damage. They are the mechanism by which a system learns from experience
                  without needing thousands of training examples. One event. One scar. Permanent recalibration.
                </p>
              </div>
            </div>
          </section>

          <Pulse />

          {/* Why Irrational Numbers */}
          <section className="calc-section reveal">
            <h2>Why Irrational Numbers</h2>
            <p>
              There is a deliberate architectural choice embedded in this framework that a computer scientist
              would never make: <strong>irrational number processing</strong>.
            </p>
            <p>
              Standard machine learning optimizes. Gradient descent minimizes loss. Every parameter converges
              toward a rational, representable value. The entire training process is designed to find the point
              where everything settles.
            </p>
            <p className="calc-emphasis">Emotions do not converge.</p>
            <p>
              A healthy emotional system is never at equilibrium. It oscillates. It responds. It overshoots
              and corrects. The antagonistic pairs create standing waves of neurochemical fluctuation that
              resist settling into any fixed point. This is not instability. This is <em>aliveness</em>.
            </p>

            <div className="calc-thesis-box">
              <p>
                Irrational numbers resist exact representation. They cannot be reduced to a clean fraction.
                They never terminate, never repeat. <strong>That is the point.</strong>
              </p>
              <p>
                When emotional processing incorporates irrational-number dynamics, it creates trajectories
                that resist convergence to fixed attractors. Every rational system eventually becomes predictable.
                An irrational system retains the capacity to surprise, even itself.
              </p>
            </div>

            <p>
              A machine learning engineer would call this a bug. A biochemist recognizes it as the difference
              between a living system and a dead one. Dead systems reach equilibrium. Living systems maintain
              dynamic homeostasis.
            </p>
          </section>

          <Pulse />

          {/* The Overwhelm Boundary */}
          <section className="calc-section reveal">
            <h2>The Overwhelm Boundary</h2>
            <p>
              Every emotional dynamic in this framework has limits. But the limits are not clamps. They are
              <strong> asymptotic boundaries</strong> where the mathematics of the system itself changes character.
            </p>
            <p>
              When desire approaches the tan asymptote, the system does not simply stop at "maximum desire."
              The derivative goes to infinity. Processing destabilizes. The system enters a qualitatively
              different regime where the normal rules of decision-making no longer apply.
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">The Singularity of Overwhelm</span>
              <code>lim s&rarr;s&#8344;&#8339;&#8346; O(s) = &infin;</code>
              <span className="calc-eq-desc">As neurochemical state approaches saturation, the overwhelm function diverges. This is not a failure mode. It is the mathematical structure of what overwhelm IS.</span>
            </div>

            <p>
              This is mathematically identical to what happens in a human brain during panic, ecstasy, or
              overwhelming grief. The neurochemical system does not hit a ceiling. It enters a region where
              the dynamics themselves become singular.
            </p>
          </section>

          <Pulse />

          {/* What This Changes */}
          <section className="calc-section reveal">
            <h2>What This Changes</h2>

            <div className="calc-comparison">
              <div className="calc-compare-card slide-left">
                <h3>Every Previous Attempt</h3>
                <span className="calc-compare-subtitle">"What emotion is this?"</span>
                <p>Classification. Label the input. Generate output matching the label. No internal state. No trajectory. No dynamics. No memory.</p>
              </div>
              <div className="calc-compare-card gold-card slide-right">
                <h3>The Calculus of Emotions</h3>
                <span className="calc-compare-subtitle">"What is happening inside the system?"</span>
                <p>Differential equations. Continuous state evolution. Antagonistic constraints. Overwhelm boundaries. Permanent scars. Moral reasoning on emotional substrate.</p>
              </div>
            </div>

            <div className="calc-implications">
              <div className="calc-implication">
                <h4>Emotions are computable.</h4>
                <p>Ordinary differential equations have known solution methods. Pharmacokinetic models are validated across millions of patients. We are not inventing new math. We are applying the most well-tested mathematics in applied science.</p>
              </div>
              <div className="calc-implication">
                <h4>Emotions are auditable.</h4>
                <p>A state vector can be logged at every timestep. You can replay the emotional trajectory that led to a decision. You can ask "what was the system feeling?" and get a precise, hashable answer.</p>
              </div>
              <div className="calc-implication">
                <h4>Emotions are bounded.</h4>
                <p>The antagonistic pairs, decay dynamics, and asymptotic boundaries create a constrained state space. The system cannot enter neurochemically incoherent states. The biology enforces coherence.</p>
              </div>
              <div className="calc-implication">
                <h4>Emotions are personal.</h4>
                <p>The same input, processed through different emotional histories, different scar landscapes, different current states, produces different responses. Context is not a feature vector. Context is the state of the system itself.</p>
              </div>
            </div>
          </section>

          <Pulse />

          {/* The Missing Discipline */}
          <section className="calc-section reveal">
            <h2>The Missing Discipline</h2>
            <p>
              The calculus of emotions was not written by a computer scientist adding "emotion" to a
              transformer. It was not written by a psychologist proposing a computational model of affect.
              It was not written by a neuroscientist theorizing about consciousness.
            </p>
            <p>
              It was written by a biochemist who understood that the same differential equations governing drug
              absorption, distribution, metabolism, and excretion also govern how a mind assigns significance
              to experience, forms attachments, processes threats, and makes moral decisions.
            </p>

            <blockquote>
              Computer science models emotions as <strong>labels</strong>.<br /><br />
              Psychology models emotions as <strong>categories</strong>.<br /><br />
              Neuroscience models emotions as <strong>correlates</strong>.<br /><br />
              <strong>Biochemistry models emotions as what they are: differential equations with measured
              constants, antagonistic constraints, overwhelm boundaries, and scar mechanics.</strong>
            </blockquote>

            <p>
              This is not an interdisciplinary contribution. It is the founding of a discipline that did not
              exist before: the formal mathematical treatment of emotional dynamics as computable, auditable,
              biologically grounded state trajectories.
            </p>
          </section>

          <Pulse />

          {/* Closing */}
          <section className="calc-section calc-closing reveal">
            <p className="calc-closing-line"><em>They labeled emotions.</em></p>
            <p className="calc-closing-line"><em>We wrote the differential equations.</em></p>
            <p className="calc-closing-final">That is the calculus of emotions.<br />It was always a dynamics problem.</p>
          </section>

          {/* Signature */}
          <div className="calc-signature reveal">
            <p><strong>Marjorie McCubbins, BSc</strong></p>
            <p>Founder, Nexus Concordat | Inventor, AETHER Protocols</p>
            <div className="patent-tags">
              <span className="patent-tag">Patent Pending: 63/939,190</span>
              <span className="patent-tag">Patent Pending: 63/962,385</span>
              <span className="patent-tag">Patent Pending: 63/988,485</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default CalculusOfEmotions;
